"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";
import { AuditForm } from "@/components/marketing/audit-form";

/**
 * La modale du bilan gratuit. Pilotée par `open` / `onClose` : aujourd'hui
 * ouverte depuis la barre du hero (audit-launcher.tsx), demain déclenchable
 * automatiquement — il suffira de passer `open` depuis ailleurs.
 *
 * Élément <dialog> natif : piège de focus, Échap et fond posés par le
 * navigateur. Rendu dans un portail sur <body> : le lanceur vit dans un bloc
 * animé (opacité, translation) et sous un `overflow: hidden`, on ne veut
 * dépendre d'aucun de ces ancêtres. Deux volets sur grand écran (l'argumentaire à gauche, le
 * formulaire à droite) ; une seule colonne, courte, sur mobile.
 * Styles : src/styles/audit-dialog.css (préfixe `bilan-`).
 */
export function AuditDialog({
  open,
  onClose,
  initialDomaine = "",
}: {
  open: boolean;
  onClose: () => void;
  initialDomaine?: string;
}) {
  const t = useTranslations("marketing.hero.audit");
  const ref = useRef<HTMLDialogElement>(null);
  const [monte, setMonte] = useState(false);
  useEffect(() => setMonte(true), []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      document.body.style.overflow = "hidden";
    } else if (!open && el.open) {
      el.close();
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Clic sur le fond : la cible est le <dialog> lui-même, pas son contenu.
  function onBackdrop(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  const checks = ["c1", "c2", "c3", "c4"] as const;
  const colors = ["var(--primary)", "var(--m-violet, #7c5cfc)", "var(--m-blue, #2e7df6)", "var(--m-amber, #e8930c)"];

  if (!monte) return null;

  return createPortal(
    <dialog
      ref={ref}
      className="bilan-dialog"
      aria-labelledby="bilan-title"
      onClose={onClose}
      onClick={onBackdrop}
    >
      <div className="bilan-frame">
      <div className="bilan-panel">
        <button type="button" className="bilan-close" onClick={onClose} aria-label={t("modal.close")}>
          <X size={18} />
        </button>

        <aside className="bilan-side">
          <div className="bilan-side-bg" aria-hidden="true"><span className="a" /><span className="b" /></div>
          <div className="bilan-corner tl" aria-hidden="true" />
          <div className="bilan-corner br" aria-hidden="true" />
          <div className="bilan-side-in">
            <h2 id="bilan-title" className="bilan-title">{t("modal.title")}</h2>
            <p className="bilan-passive">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              {t("modal.passive")}
            </p>
            <p className="bilan-intro">{t("modal.intro")}</p>
            <ul className="bilan-checks">
              {checks.map((c, i) => (
                <li key={c}>
                  <span className="bilan-chip" style={{ "--c": colors[i] } as React.CSSProperties}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                  </span>
                  {t(`modal.${c}`)}
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="bilan-main">
          {open && (
            <AuditForm
              key={initialDomaine}
              initialDomaine={initialDomaine}
              framed={false}
              showHeading={false}
              autoFocusEmail={initialDomaine.trim().length > 0}
            />
          )}
        </div>
      </div>
      </div>
    </dialog>,
    document.body,
  );
}
